/*
 * Toplu SIM ekleme modali
 */

import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Text,
  Select,
  FormHelperText,
  useToast,
  VStack,
} from "@chakra-ui/react";
import api from "utils/api";
import { getCurrentUser } from "services/authService";

const parseBulkInput = (raw) =>
  raw
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => line.split(/[,;\t]+/).map((part) => part.trim()));

export default function SimBulkUploadModal({ isOpen, onClose, onSuccess, bayiler = [] }) {
  const [rowsText, setRowsText] = useState("");
  const [satinAlinanTarih, setSatinAlinanTarih] = useState("");
  const [bayiId, setBayiId] = useState("");
  const [userRole, setUserRole] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  useEffect(() => {
    getCurrentUser()
      .then((user) => {
        setUserRole(user.role);
      })
      .catch(() => {
        setUserRole(null);
      });
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setRowsText("");
      setSatinAlinanTarih("");
      setBayiId("");
      setSubmitting(false);
    }
  }, [isOpen]);

  const rowsPreview = useMemo(() => {
    try {
      return parseBulkInput(rowsText);
    } catch {
      return [];
    }
  }, [rowsText]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rowsPreview.length || !satinAlinanTarih) {
      toast({
        title: "Eksik bilgi",
        description: "Tarih ve en az bir satır giriniz.",
        status: "warning",
      });
      return;
    }

    const malformed = rowsPreview.find((cols) => cols.length < 3);
    if (malformed) {
      toast({
        title: "Format hatası",
        description: "Her satır 'iccid, imsi, tel' şeklinde olmalıdır.",
        status: "error",
      });
      return;
    }

    try {
      setSubmitting(true);
      const items = rowsPreview.map(([iccid, imsi, tel_no]) => ({
        iccid,
        imsi,
        tel_no,
      }));
      const payload = {
        satin_alinan_tarih: new Date(satinAlinanTarih).toISOString(),
        items,
      };
      
      // Sadece admin için bayi_id gönder (saas_bayi için backend otomatik atar)
      if (userRole === "admin" && bayiId && bayiId.trim() !== "") {
        const parsedBayiId = parseInt(bayiId);
        if (!isNaN(parsedBayiId)) {
          payload.bayi_id = parsedBayiId;
        }
      }
      
      const res = await api.post("/api/sim-kartlari/bulk", payload);
      const json = await res.json();
      if (!res.ok) {
        // Validation hatası için kullanıcı dostu mesaj göster
        let errorMessage = "Toplu ekleme başarısız.";
        if (res.status === 422 && json?.detail) {
          // Pydantic validation hatası
          if (Array.isArray(json.detail)) {
            // Sadece karakter kısıtlamalarını topla
            const fieldNames = {
              'iccid': 'ICCID',
              'imsi': 'IMSI',
              'tel_no': 'Telefon numarası'
            };
            const minLengths = {
              'iccid': 10,
              'imsi': 10,
              'tel_no': 11
            };
            const errors = [];
            json.detail.forEach(err => {
              const field = err.loc?.[err.loc.length - 1] || '';
              const fieldName = fieldNames[field] || field;
              
              // Minimum uzunluk hatası
              if (err.type === 'value_error.any_str.min_length' || err.msg?.includes('at least') || err.msg?.includes('characters')) {
                const minLength = minLengths[field] || err.ctx?.limit || 5;
                errors.push(`${fieldName} en az ${minLength} karakter olmalıdır`);
              }
              // Maksimum uzunluk hatası
              else if (err.type === 'value_error.any_str.max_length' || err.msg?.includes('at most')) {
                const maxLength = err.ctx?.limit || 40;
                errors.push(`${fieldName} en fazla ${maxLength} karakter olabilir`);
              }
              // Zorunlu alan hatası
              else if (err.type === 'value_error.missing' || err.msg?.includes('required')) {
                errors.push(`${fieldName} zorunludur`);
              }
            });
            
            if (errors.length > 0) {
              // Tekrarları kaldır ve birleştir
              errorMessage = [...new Set(errors)].join('. ');
            } else {
              errorMessage = "Girilen veriler geçersiz. Lütfen kontrol edin.";
            }
          } else if (typeof json.detail === 'string') {
            errorMessage = json.detail;
          }
        } else if (json?.detail) {
          errorMessage = json.detail;
        }
        throw new Error(errorMessage);
      }
      toast({
        title: "Toplu ekleme tamamlandı",
        description: `${json.inserted} SIM eklendi, ${json.skipped.length} satır atlandı.`,
        status: "success",
      });
      onSuccess?.(json.inserted);
      onClose();
    } catch (error) {
      toast({
        title: "Hata",
        description: error.message,
        status: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit}>
        <ModalHeader>Toplu SIM Yükle</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <FormControl isRequired>
              <FormLabel>Satın Alınan Tarih</FormLabel>
              <Input
                type="datetime-local"
                value={satinAlinanTarih}
                onChange={(e) => setSatinAlinanTarih(e.target.value)}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>SIM Satırları</FormLabel>
              <Textarea
                value={rowsText}
                onChange={(e) => setRowsText(e.target.value)}
                placeholder="iccid,imsi,tel_no"
                rows={8}
              />
              <Text fontSize="sm" color="gray.500" mt={1}>
                Her satır: <strong>iccid, imsi, tel_no</strong>. Ayraç olarak virgül, noktalı virgül veya tab kullanılabilir.
              </Text>
              <VStack align="flex-start" spacing={1} mt={1} fontSize="xs" color="gray.500">
                <Text fontWeight="semibold">Örnekler:</Text>
                <Text as="code">8944531234567890123,286015123456789,05321234567</Text>
                <Text as="code">8986012345678901234,286012987654321,05001234567</Text>
                <Text as="code">8960123412341234123,286011112223334,05411223344</Text>
              </VStack>
            </FormControl>
            <Text fontSize="xs" color="gray.500">
              Önizleme: {rowsPreview.length} satır
            </Text>
            {userRole === "admin" && (
              <FormControl>
                <FormLabel>Bayi (Opsiyonel)</FormLabel>
                <Select
                  placeholder="Bayi seçiniz (boş bırakılırsa admin'e eklenir)"
                  value={bayiId}
                  onChange={(e) => setBayiId(e.target.value)}
                >
                  {bayiler.map((bayi) => (
                    <option key={bayi.id} value={bayi.id}>
                      {bayi.name}
                    </option>
                  ))}
                </Select>
                <FormHelperText fontSize="xs" color="gray.500">
                  SIM kayıtlarını bir bayiye atamak için seçiniz. Boş bırakılırsa admin'e eklenir.
                </FormHelperText>
              </FormControl>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button onClick={onClose} variant="ghost">
            Vazgeç
          </Button>
          <Button colorScheme="blue" type="submit" isLoading={submitting}>
            Kaydet
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

